// Updated FeedbackWidget.js
import React, { useState } from 'react';
import { Button, TextField, Rating, Dialog, DialogTitle, 
         DialogContent, DialogActions, Typography, Snackbar, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../authentication/AuthContext';

const FeedbackWidget = () => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const submitFeedback = async () => {
    if (!rating) return;
    
    setLoading(true);
    try {
      // Google Apps Script web app URL (deploy as web app - anyone can access)
      const sheetEndpoint = process.env.REACT_APP_FEEDBACK_SHEET_URL;
      
      const data = {
        timestamp: new Date().toISOString(),
        userId: user?.username || 'anonymous',
        email: user?.email || 'anonymous',
        rating,
        feedback,
        page: window.location.pathname,
        userAgent: navigator.userAgent,
        subscriptionType: user?.subscription || 'none'
      };
      
      // Send data to Google Sheets
      await fetch(sheetEndpoint, {
        method: 'POST',
        mode: 'no-cors', // Important for cross-origin requests
        cache: 'no-cache',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
      
      setSuccess(true);
      setOpen(false);
      setRating(0);
      setFeedback('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button 
        variant="contained" 
        sx={{ 
          position: 'fixed', 
          bottom: '20px', 
          color: 'var(--background-color)',
          backgroundColor: 'var(--tertiary-color)',
          right: '20px',
          fontSize: '8px',
          borderRadius: '24px',
          padding: '8px 16px',
          zIndex: 500
        }}
        onClick={() => setOpen(true)}
      >
        Give Feedback
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Share Your Feedback</DialogTitle>
        <DialogContent>
          <Typography component="legend">How would you rate your experience?</Typography>
          <Rating
            value={rating}
            onChange={(_, value) => setRating(value)}
            size="large"
            sx={{ my: 2 }}
          />
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="Your feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            sx={{ mt: 2 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontSize: '0.8rem' }}>
            Your feedback is important to us. View all community feedback on our{' '}
            <Link component={RouterLink} to="/feedback">feedback page</Link>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button 
            onClick={submitFeedback} 
            variant="contained" 
            disabled={!rating || loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={success}
        autoHideDuration={4000}
        onClose={() => setSuccess(false)}
        message="Thank you for your feedback! View all feedback on our feedback page."
        action={
          <Button 
            color="primary" 
            size="small" 
            component={RouterLink} 
            to="/feedback"
            onClick={() => setSuccess(false)}
          >
            View
          </Button>
        }
      />
    </>
  );
};

export default FeedbackWidget;